import React,{useState}from"react";
import{Box,Button,Dialog,DialogActions,DialogContent,DialogTitle,Stack,TextField,Typography}from"@mui/material";
import AddRoundedIcon from"@mui/icons-material/AddRounded";
import RemoveRoundedIcon from"@mui/icons-material/RemoveRounded";
import{useAlert}from"../../../context/AlertContext";

function getToken(){
  return localStorage.getItem("accessToken")||localStorage.getItem("access")||"";
}

function normalizeDecimalInput(value){
  let normalized=String(value??"").replace(",",".").replace(/[^\d.]/g,"");
  const parts=normalized.split(".");
  if(parts.length>2)normalized=`${parts.shift()}.${parts.join("")}`;
  if(normalized.includes(".")){
    const[integer,decimal]=normalized.split(".");
    normalized=`${integer}.${decimal.slice(0,2)}`;
  }
  return normalized;
}

function errorMessage(data,fallback){
  const value=data?.error||data?.detail;
  if(typeof value==="string")return value;
  if(value&&typeof value==="object"){
    return Object.values(value).flat().map(item=>typeof item==="string"?item:JSON.stringify(item)).join(" ");
  }
  return fallback;
}

export default function RegisterMovements({dashboardTheme,cajaAbierta,onReload,canEdit=true}){
  const{showSuccess,showError}=useAlert();
  const[open,setOpen]=useState(false);
  const[tipo,setTipo]=useState("INGRESO");
  const[monto,setMonto]=useState("");
  const[concepto,setConcepto]=useState("");
  const[observacion,setObservacion]=useState("");
  const[saving,setSaving]=useState(false);

  const resetForm=()=>{
    setMonto("");
    setConcepto("");
    setObservacion("");
  };

  const openMovement=movementType=>{
    setTipo(movementType);
    resetForm();
    setOpen(true);
  };

  const closeMovement=()=>{
    if(saving)return;
    setOpen(false);
    resetForm();
  };

  const saveMovement=async()=>{
    if(!cajaAbierta){
      showError("No tienes una caja abierta");
      return;
    }

    const amount=Number(String(monto||"").replace(",","."));
    if(!monto||!Number.isFinite(amount)||amount<=0){
      showError("Ingresa un monto válido");
      return;
    }

    if(!concepto.trim()){
      showError("Ingresa el motivo del movimiento");
      return;
    }

    try{
      setSaving(true);

      const response=await fetch("/api/ventas/caja/movimiento-manual/",{
        method:"POST",
        headers:{
          "Content-Type":"application/json",
          Authorization:`Bearer ${getToken()}`,
        },
        credentials:"include",
        body:JSON.stringify({
          tipo,
          monto:Number(amount).toFixed(2),
          concepto:concepto.trim(),
          observacion:observacion.trim(),
        }),
      });

      const data=await response.json();

      if(!response.ok){
        showError(errorMessage(data,"No se pudo registrar el movimiento"));
        return;
      }

      showSuccess(data.mensaje||(tipo==="INGRESO"?"Ingreso registrado correctamente":"Retiro registrado correctamente"));
      setOpen(false);
      resetForm();

      if(onReload)await onReload();
    }catch{
      showError("Error de conexión");
    }finally{
      setSaving(false);
    }
  };

  if(!canEdit||!cajaAbierta)return null;

  const outlinedButtonSx={
    minHeight:40,
    px:2,
    borderRadius:"12px",
    textTransform:"none",
    fontWeight:700,
    color:dashboardTheme.button_color,
    borderColor:dashboardTheme.button_color,
    boxShadow:"none",
    "&:hover":{
      borderColor:dashboardTheme.button_color,
      bgcolor:`${dashboardTheme.button_color}12`,
      boxShadow:"none",
    },
  };

  const dialogPaperSx={
    borderRadius:"16px",
    bgcolor:dashboardTheme.card_background,
    color:dashboardTheme.text_color,
    border:`1px solid ${dashboardTheme.border_color}`,
    boxShadow:"none",
    width:"100%",
    maxWidth:480,
  };

  return(
    <>
      <Stack direction={{xs:"column",sm:"row"}} spacing={1}>
        <Button
          variant="outlined"
          startIcon={<AddRoundedIcon/>}
          onClick={()=>openMovement("INGRESO")}
          sx={outlinedButtonSx}
        >
          Ingresar dinero
        </Button>

        <Button
          variant="outlined"
          startIcon={<RemoveRoundedIcon/>}
          onClick={()=>openMovement("EGRESO")}
          sx={outlinedButtonSx}
        >
          Retirar dinero
        </Button>
      </Stack>

      <Dialog
        open={open}
        onClose={closeMovement}
        fullWidth
        maxWidth="sm"
        PaperProps={{sx:dialogPaperSx}}
      >
        <DialogTitle sx={{pb:1.2}}>
          <Typography sx={{fontSize:20,fontWeight:700,color:dashboardTheme.text_color}}>
            {tipo==="INGRESO"?"Ingresar dinero":"Retirar dinero"}
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={1.5} sx={{pt:.5}}>
            <TextField
              fullWidth
              size="small"
              label="Monto"
              value={monto}
              onChange={event=>setMonto(normalizeDecimalInput(event.target.value))}
              inputMode="decimal"
              sx={dashboardTheme.inputStyles}
            />

            <TextField
              fullWidth
              size="small"
              label="Motivo"
              value={concepto}
              onChange={event=>setConcepto(event.target.value)}
              inputProps={{maxLength:200}}
              sx={dashboardTheme.inputStyles}
            />

            <TextField
              fullWidth
              size="small"
              label="Observación"
              value={observacion}
              onChange={event=>setObservacion(event.target.value)}
              multiline
              minRows={3}
              sx={dashboardTheme.inputStyles}
            />

            <Box
              sx={{
                px:1.4,
                py:1.1,
                borderRadius:"10px",
                border:`1px solid ${dashboardTheme.border_color}`,
                bgcolor:dashboardTheme.dashboard_background,
              }}
            >
              <Typography sx={{fontSize:12.5,fontWeight:500,color:dashboardTheme.muted_text_color}}>
                {tipo==="INGRESO"
                  ?"El monto se sumará al efectivo disponible de la caja abierta."
                  :"El monto se descontará del efectivo disponible de la caja abierta."}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{px:3,pb:2.5,pt:1}}>
          <Button
            onClick={closeMovement}
            disabled={saving}
            sx={{
              textTransform:"none",
              fontWeight:700,
              color:dashboardTheme.muted_text_color,
            }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={saveMovement}
            disabled={saving}
            sx={dashboardTheme.buttonStyles.contained}
          >
            {saving
              ?"Guardando..."
              :tipo==="INGRESO"
                ?"Registrar ingreso"
                :"Registrar retiro"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}